/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState } from 'react';

import Avatar from '@atlaskit/avatar';
import { cssMap, jsx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import EditIcon from '@atlaskit/icon/core/edit';
import { Box, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ActionItem } from '../action-item/ActionItem';
import { VIEW_TYPE } from '../types';

import type { PersonGroupProps } from './types';

const AVATAR_SIZE = 24;

const styles = cssMap({
	gridContainer: {
		display: 'grid',
		gridTemplateColumns: `${AVATAR_SIZE}px 1fr auto`,
		alignItems: 'center',
		rowGap: 0,
	},
	trigger: {
		gridColumn: '1 / -1',
		display: 'grid',
		gridTemplateColumns: `${AVATAR_SIZE}px 1fr auto`,
		alignItems: 'center',
		width: '100%',
		paddingBlock: token('space.100'),
		paddingInline: 0,
		textAlign: 'left',
		backgroundColor: 'transparent',
		border: 'none',
	},
	avatarCell: {
		gridColumn: 1,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	contentCell: {
		gridColumn: 2,
		paddingLeft: token('space.100'),
	},
	chevronCell: {
		gridColumn: 3,
		color: token('color.text.subtle'),
		paddingRight: token('space.050'),
	},
	actionsWrapper: {
		gridColumn: '1 / -1',
		display: 'flex',
		flexDirection: 'column',
		paddingLeft: token('space.025'),
	},
});

export function PersonGroup({
	group,
	user,
	isRenameGroup = false,
	defaultExpanded = false,
	selectedKeys,
	onSelectionChange,
	workById,
	onConfirm,
	onCancel,
	getStatus,
	hydratedUsers,
}: PersonGroupProps): React.JSX.Element {
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);
	const displayName = user.name?.trim() ?? '';

	const handleToggle = () => setIsExpanded((e) => !e);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			handleToggle();
		}
	};

	return (
		<Box xcss={styles.gridContainer}>
			<Pressable
				xcss={styles.trigger}
				onClick={handleToggle}
				onKeyDown={handleKeyDown}
				tabIndex={0}
				aria-expanded={isExpanded}
			>
				<Box xcss={styles.avatarCell}>
					{isRenameGroup ? (
						<EditIcon label="" size="medium" color={token('color.icon')} />
					) : (
						<Avatar name={displayName} src={user.picture ?? undefined} size="small" />
					)}
				</Box>
				<Box xcss={styles.contentCell}>
					<Text size="medium" weight="medium">
						{displayName}
					</Text>
				</Box>
				<Box xcss={styles.chevronCell}>
					{isExpanded ? (
						<ChevronDownIcon label="" size="small" />
					) : (
						<ChevronRightIcon label="" size="small" />
					)}
				</Box>
			</Pressable>
			{isExpanded && (
				<Box xcss={styles.actionsWrapper}>
					{group.actions.map((action) => (
						<ActionItem
							key={action.invocationId}
							action={action}
							payload={action.data}
							isChecked={selectedKeys.has(action.invocationId)}
							onToggle={(checked) => onSelectionChange(action.invocationId, checked)}
							workById={workById}
							onConfirm={onConfirm}
							onCancel={onCancel}
							getStatus={getStatus}
							viewType={VIEW_TYPE.PEOPLE}
							hydratedUsers={hydratedUsers}
						/>
					))}
				</Box>
			)}
		</Box>
	);
}
